import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  CustomerTicket,
  MyTicketsResponse,
  TicketDetailResponse,
  TicketHistoryResponse,
  TicketProduct,
  TicketType,
  TicketStatus
} from '../interfaces/ticket';

export interface AvailableTicketsResponse {
  status: string;
  tickets: TicketProduct[];
  pagination: {
    total: number;
    page: number;
    pages: number;
    size: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface TicketProductDetailResponse {
  status: string;
  ticket: TicketProduct;
  businessHub?: {
    id: number;
    name: string;
    logoUrl?: string;
    address?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TicketService {
  private readonly API_URL = `${environment.apiUrl}/experience/tickets`;

  constructor(private http: HttpClient) {}

  /**
   * Get all tickets for the current user
   */
  getMyTickets(status?: TicketStatus, ticketType?: TicketType): Observable<MyTicketsResponse> {
    let url = `${this.API_URL}/my-tickets`;
    const params: string[] = [];

    if (status) {
      params.push(`status=${status}`);
    }
    if (ticketType) {
      params.push(`ticketType=${ticketType}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return this.http.get<MyTicketsResponse>(url).pipe(
      map(response => this.mapTicketsResponse(response)),
      catchError(error => {
        console.error('Error fetching tickets:', error);
        return of({
          status: 'error',
          tickets: [],
          grouped: { active: [], expired: [], exhausted: [], cancelled: [] },
          counts: { total: 0, active: 0, expired: 0, exhausted: 0, cancelled: 0 }
        });
      })
    );
  }

  /**
   * Get a single ticket by ID with QR code data
   */
  getTicket(ticketId: number): Observable<TicketDetailResponse | null> {
    return this.http.get<TicketDetailResponse>(`${this.API_URL}/${ticketId}`).pipe(
      map(response => this.mapTicketDetailResponse(response)),
      catchError(error => {
        console.error('Error fetching ticket:', error);
        return of(null);
      })
    );
  }

  /**
   * Get redemption history for a ticket
   */
  getTicketHistory(ticketId: number): Observable<TicketHistoryResponse | null> {
    return this.http.get<TicketHistoryResponse>(`${this.API_URL}/${ticketId}/history`).pipe(
      catchError(error => {
        console.error('Error fetching ticket history:', error);
        return of(null);
      })
    );
  }

  // ========== Helper Methods ==========

  private mapTicketsResponse(response: MyTicketsResponse): MyTicketsResponse {
    return {
      ...response,
      tickets: response.tickets.map(t => this.mapTicket(t)),
      grouped: {
        active: response.grouped.active.map(t => this.mapTicket(t)),
        expired: response.grouped.expired.map(t => this.mapTicket(t)),
        exhausted: response.grouped.exhausted.map(t => this.mapTicket(t)),
        cancelled: response.grouped.cancelled.map(t => this.mapTicket(t))
      }
    };
  }

  private mapTicketDetailResponse(response: TicketDetailResponse): TicketDetailResponse {
    return {
      ...response,
      ticket: this.mapTicket(response.ticket)
    };
  }

  private mapTicket(ticket: CustomerTicket): CustomerTicket {
    return {
      ...ticket,
      purchasedAt: ticket.purchasedAt,
      validFrom: ticket.validFrom,
      validUntil: ticket.validUntil,
      firstUsedAt: ticket.firstUsedAt,
      lastUsedAt: ticket.lastUsedAt
    };
  }

  /**
   * Get display label for ticket type (German)
   */
  getTicketTypeLabel(type: TicketType | string): string {
    const labels: Record<string, string> = {
      'stamp_card': 'Stempelkarte',
      'event_ticket': 'Event-Ticket',
      'membership': 'Mitgliedschaft'
    };
    return labels[type] || type;
  }

  /**
   * Get display label for ticket status (German)
   */
  getStatusLabel(status: TicketStatus | string): string {
    const labels: Record<string, string> = {
      'active': 'Aktiv',
      'expired': 'Abgelaufen',
      'exhausted': 'Aufgebraucht',
      'cancelled': 'Storniert'
    };
    return labels[status] || status;
  }

  /**
   * Get icon for ticket type
   */
  getTicketTypeIcon(type: TicketType | string): string {
    const icons: Record<string, string> = {
      'stamp_card': 'pi pi-th-large',
      'event_ticket': 'pi pi-calendar',
      'membership': 'pi pi-id-card'
    };
    return icons[type] || 'pi pi-ticket';
  }

  /**
   * Get color class for ticket status
   */
  getStatusColor(status: TicketStatus | string): string {
    const colors: Record<string, string> = {
      'active': 'bg-green-100 text-green-800',
      'expired': 'bg-yellow-100 text-yellow-800',
      'exhausted': 'bg-blue-100 text-blue-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  // ========== Shop/Browse Methods ==========

  /**
   * Get available tickets for purchase (for shop/events view)
   */
  getAvailableTickets(
    page: number = 1,
    size: number = 20,
    ticketType?: TicketType,
    businessHubId?: number,
    search?: string
  ): Observable<AvailableTicketsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (ticketType) {
      params = params.set('ticketType', ticketType);
    }
    if (businessHubId) {
      params = params.set('businessHubId', businessHubId.toString());
    }
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<AvailableTicketsResponse>(`${this.API_URL}/available`, { params }).pipe(
      catchError(error => {
        console.error('Error fetching available tickets:', error);
        return of({
          status: 'error',
          tickets: [],
          pagination: { total: 0, page: 1, pages: 0, size: 20, hasNext: false, hasPrev: false }
        });
      })
    );
  }

  /**
   * Get ticket product details for purchase
   */
  getTicketProductDetail(ticketProductId: number): Observable<TicketProductDetailResponse | null> {
    return this.http.get<TicketProductDetailResponse>(`${this.API_URL}/product/${ticketProductId}`).pipe(
      catchError(error => {
        console.error('Error fetching ticket product detail:', error);
        return of(null);
      })
    );
  }

  /**
   * Convert a TicketProduct to a Product-like object for cart compatibility
   */
  ticketProductToCartProduct(ticketProduct: TicketProduct, businessHubId: number): any {
    return {
      id: ticketProduct.product?.id,
      name: ticketProduct.product?.name,
      description: ticketProduct.product?.description,
      price: ticketProduct.product?.price || 0,
      currency: ticketProduct.product?.currency || 'EUR',
      imageUrl: ticketProduct.product?.imageUrl,
      smartCoins: ticketProduct.product?.smartCoins || 0,
      category: 'Tickets',
      business_hub: { id: businessHubId },
      // Ticket-specific info
      ticketProductId: ticketProduct.id,
      ticketType: ticketProduct.ticketType,
      eventDate: ticketProduct.eventDate,
      eventLocation: ticketProduct.eventLocation,
      isTicket: true
    };
  }
}
